/**
 * Pillar Challenges — engine.
 *
 * Three responsibilities:
 *   1. PERSISTENCE — Firestore CRUD for challenges + their daily status docs.
 *   2. AUTO-DETECTION — for each commitment type, query existing OPMODE
 *      data sources and decide if it's done today.
 *   3. EVALUATION — roll auto + manual checks into a DailyStatus,
 *      compute pillar counts + outcome, persist.
 *
 * Called from:
 *   - app/challenge/page.tsx (setup/create)
 *   - components/dashboard/PillarMissionCard.tsx (read + manual checks)
 *   - midnight rollover hook in AuthContext (eval + lock-in)
 *
 * NOT in this file:
 *   - The types live in lib/pillarChallenges.ts.
 *   - The UI lives in components/.../*.tsx and app/challenge/page.tsx.
 *   - Notifications wire from a separate scheduler module.
 */

import {
  type Challenge,
  type DailyStatus,
  type Commitment,
  type Pillar,
  type Preset,
  COMMITMENT_LIBRARY,
  MAX_SWAPS,
  getCommitment,
  getPreset,
  challengeCommitments,
  computeDayOutcome,
  getWeeklyFrequency,
  isDailyRequired,
} from './pillarChallenges';

// ============================================================================
// LOCAL DATE HELPERS
// ============================================================================

/** YYYY-MM-DD in local time. Used for day keys + wake-time comparisons. */
function localDateString(d: Date = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Parse a "YYYY-MM-DD" string into a LOCAL-midnight Date.
 *
 * CRITICAL: `new Date("2026-05-28")` parses as UTC midnight, which in
 * any negative-UTC timezone (all of the Americas) lands on the PREVIOUS
 * calendar day in local time. That off-by-one silently broke the
 * RestartGate "don't fire before start date" guard — after a restart
 * set startDate to today, re-parsing it shifted to yesterday, so the
 * failed-day modal kept reappearing and "Restart didn't work."
 *
 * Splitting + constructing with (y, m-1, d) forces local interpretation.
 */
export function parseLocalDate(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

/**
 * The [start, end) instant window covering the LOCAL calendar day
 * `dateStr`. Start is local midnight of that day, end is local midnight
 * of the next day — computed by incrementing the date component, not by
 * adding 86_400_000ms, so a DST transition day is still exactly one
 * calendar day (23h or 25h) rather than spilling an hour into a
 * neighbouring day.
 *
 * Exported for tests: this is the boundary every dated data-source
 * adapter filters on, and the whole class of "workout logged at 09:00
 * counted for yesterday" bugs lives here.
 */
export function localDayWindow(dateStr: string): { start: Date; end: Date } {
  const start = parseLocalDate(dateStr);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  end.setHours(0, 0, 0, 0);
  return { start, end };
}

/** Calculate the day number (1-indexed) for `date` within `challenge`. */
export function dayNumberFor(challenge: Pick<Challenge, 'startDate'>, date: Date = new Date()): number {
  const start = parseLocalDate(challenge.startDate);
  start.setHours(0, 0, 0, 0);
  const now = new Date(date);
  now.setHours(0, 0, 0, 0);
  const diffMs = now.getTime() - start.getTime();
  return Math.floor(diffMs / 86_400_000) + 1;
}

// ============================================================================
// PERSISTENCE — Firestore CRUD
// ============================================================================

const COLLECTION = 'challenges';

/**
 * Create a new Challenge from a preset (or blank slate). Auto-generates
 * id, sets initial counters, persists to Firestore.
 */
export async function createChallenge(
  userId: string,
  base: Omit<Challenge, 'id' | 'userId' | 'attempt' | 'totalDaysCompleted' | 'currentStreak' | 'longestStreak' | 'createdAt' | 'updatedAt' | 'status'>
): Promise<Challenge> {
  const { doc, collection, setDoc, serverTimestamp } = await import('firebase/firestore');
  const { db } = await import('./firebase');
  const ref = doc(collection(db, 'users', userId, COLLECTION));
  const now = new Date().toISOString();
  const challenge: Challenge = {
    ...base,
    id: ref.id,
    userId,
    status: 'active',
    attempt: 1,
    totalDaysCompleted: 0,
    currentStreak: 0,
    longestStreak: 0,
    createdAt: now,
    updatedAt: now,
  };
  await setDoc(ref, { ...challenge, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
  // Fire-and-forget initial reminder schedule. evaluateToday will
  // refresh the schedule with real item counts on first evaluation;
  // this just gets the morning briefing into the OS queue immediately.
  try {
    const { scheduleChallengeNotifications } = await import('./pillarChallengeNotifications');
    void scheduleChallengeNotifications(challenge, null);
  } catch (e) {
    console.warn('[challenges] initial notification schedule failed:', e);
  }
  emitChallengeChanged();
  return challenge;
}

/**
 * Fetch the user's current LIVE challenge, if any.
 *
 * "Live" means an arc that still owns the user's slot: 'active',
 * 'paused' or 'failed'. Only 'completed' is excluded — that arc is
 * genuinely over and the user is free to start a new one.
 *
 * WHY paused/failed are included: they used to be filtered out, so a
 * user whose arc was paused or dead got `null` back and every consumer
 * concluded "no challenge" — /challenge offered them the preset picker
 * and let them create a DUPLICATE on top of the one they still had.
 * The failed screen (status === 'failed') was likewise unreachable.
 *
 * CALLERS MUST CHECK `status` THEMSELVES if they need a live arc —
 * this no longer guarantees 'active'.
 *
 * IMPORTANT: this used to query with `where(status) + orderBy(createdAt)
 * + limit(1)`, which Firestore requires a composite index for. Without
 * the index deployed, the query throws → caught by the try/catch →
 * returns null → dashboard shows "start a challenge" even though a
 * challenge is active. That was a critical bug.
 *
 * Now we do a single `where(status, 'in', [...])` — no orderBy, no
 * limit — and sort client-side. An `in` filter on one field decomposes
 * into parallel equality queries served by the automatic single-field
 * index, so this still costs NO composite index. A user with 1 live
 * challenge (the typical case) sees no perf difference; a user with
 * several gets all of them and we return the newest.
 */
export async function getActiveChallenge(userId: string): Promise<Challenge | null> {
  try {
    const { collection, getDocs, query, where } = await import('firebase/firestore');
    const { db } = await import('./firebase');
    const ref = collection(db, 'users', userId, COLLECTION);
    const q = query(ref, where('status', 'in', ['active', 'paused', 'failed']));
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const challenges = snap.docs.map(d => d.data() as Challenge);
    // Newest-first by createdAt. Field can be either an ISO string
    // (locally-built on write) or a Firestore Timestamp (server-side
    // resolved value). Normalize to ms for compare.
    const toMs = (v: unknown): number => {
      if (!v) return 0;
      if (typeof v === 'string') {
        const t = Date.parse(v);
        return isNaN(t) ? 0 : t;
      }
      if (typeof (v as any).toMillis === 'function') return (v as any).toMillis();
      if (typeof (v as any).toDate === 'function') return (v as any).toDate().getTime();
      return 0;
    };
    // Rank before recency: a genuinely active arc must always beat a
    // paused or dead one, even if the dead one was created later.
    // Within a rank, newest wins (the previous behaviour).
    const rank = (c: Challenge): number =>
      c.status === 'active' ? 0 : c.status === 'paused' ? 1 : 2;
    challenges.sort((a, b) => (rank(a) - rank(b)) || (toMs(b.createdAt) - toMs(a.createdAt)));
    return challenges[0];
  } catch (err) {
    console.warn('[challenges] getActiveChallenge failed:', err);
    return null;
  }
}

export interface WeeklyProgressEntry {
  commitmentId: string;
  /** Days completed in the current ISO week (Mon-Sun). */
  done: number;
  /** Weekly target (1-6). */
  target: number;
  /** True once done >= target. */
  met: boolean;
}

/**
 * Compute weekly-target progress for the current ISO week (Mon-Sun).
 *
 * For each commitment configured with a weeklyFrequency, counts how
 * many days this week it was marked complete. The PillarMissionCard
 * renders these as progress bars ("Workout 2/3 this week"). A weekly
 * target is "met" once done >= target; until then it's neither passed
 * nor failed on any individual day.
 */
export async function computeWeeklyProgress(
  userId: string,
  challenge: Challenge,
  now: Date = new Date(),
): Promise<WeeklyProgressEntry[]> {
  // Identify weekly commitments up front; bail cheaply if none.
  const weeklyIds = challengeCommitments(challenge)
    .map(c => c.id)
    .filter(id => getWeeklyFrequency(id, challenge) !== null);
  if (weeklyIds.length === 0) return [];

  // Current ISO week bounds: Monday 00:00 → now.
  const monday = new Date(now);
  const dow = (monday.getDay() + 6) % 7; // 0 = Monday
  monday.setDate(monday.getDate() - dow);
  monday.setHours(0, 0, 0, 0);
  const inWeek = (dateStr: string): boolean => {
    const [y, m, d] = dateStr.split('-').map(Number);
    const dt = new Date(y, (m ?? 1) - 1, d ?? 1);
    return dt >= monday && dt <= now;
  };

  // Pull recent statuses (30d window comfortably covers a 7-day week)
  // and tally completion per weekly commitment within this week.
  const recent = await getRecentDailyStatuses(userId, challenge.id, 30);
  const counts = new Map<string, number>();
  for (const status of recent) {
    if (!inWeek(status.date)) continue;
    for (const id of weeklyIds) {
      if (status.completed?.[id]) counts.set(id, (counts.get(id) ?? 0) + 1);
    }
  }

  return weeklyIds.map(id => {
    const target = getWeeklyFrequency(id, challenge) ?? 1;
    const done = counts.get(id) ?? 0;
    return { commitmentId: id, done, target, met: done >= target };
  });
}

/** Save a partial update to a challenge. */
export async function updateChallenge(userId: string, challengeId: string, patch: Partial<Challenge>): Promise<void> {
  const { doc, updateDoc, serverTimestamp } = await import('firebase/firestore');
  const { db } = await import('./firebase');
  const ref = doc(db, 'users', userId, COLLECTION, challengeId);
  await updateDoc(ref, { ...patch, updatedAt: serverTimestamp() } as any);

  // Status transitions affect notifications: pause/complete/fail must
  // silence pending reminders, resume must put them back. We cancel
  // unconditionally on any status change and let evaluateToday's next
  // call re-arm on resume (status==='active' will trigger a fresh
  // schedule there). Notification-preference changes (morningBriefing
  // / eveningWarning / finalCall toggles in the patch) likewise need
  // a re-schedule — easiest to just cancel and let evaluateToday do
  // the rebuild on the next refresh tick.
  if ('status' in patch || 'notifications' in patch) {
    try {
      const { cancelChallengeNotifications } = await import('./pillarChallengeNotifications');
      void cancelChallengeNotifications();
    } catch {}
  }
}

/** Load a day's status document. Returns null if not yet created. */
export async function getDailyStatus(userId: string, challengeId: string, dateStr: string): Promise<DailyStatus | null> {
  try {
    const { doc, getDoc } = await import('firebase/firestore');
    const { db } = await import('./firebase');
    const ref = doc(db, 'users', userId, COLLECTION, challengeId, 'days', dateStr);
    const snap = await getDoc(ref);
    return snap.exists() ? (snap.data() as DailyStatus) : null;
  } catch (err) {
    console.warn('[challenges] getDailyStatus failed:', err);
    return null;
  }
}

/**
 * Read the last N day-status docs (newest first), used by failure-
 * pattern detection. Bounded by `limit` so we never page through a
 * 75-day challenge's full history when the pattern code only needs
 * 30 days to make a call.
 */
export async function getRecentDailyStatuses(
  userId: string,
  challengeId: string,
  limitDays: number = 30,
): Promise<DailyStatus[]> {
  try {
    const { collection, query, orderBy, limit: fsLimit, getDocs } = await import('firebase/firestore');
    const { db } = await import('./firebase');
    const ref = collection(db, 'users', userId, COLLECTION, challengeId, 'days');
    const q = query(ref, orderBy('date', 'desc'), fsLimit(limitDays));
    const snap = await getDocs(q);
    return snap.docs.map(d => d.data() as DailyStatus);
  } catch (err) {
    console.warn('[challenges] getRecentDailyStatuses failed:', err);
    return [];
  }
}

/**
 * A day doc plus the map recording commitments the user has EXPLICITLY
 * un-checked today.
 *
 * WHY A SIBLING MAP AND NOT A TOMBSTONE INSIDE `completed`:
 * an absent `completed[id]` is indistinguishable from "never detected",
 * so un-checking an auto-detectable commitment (steps, sleep, session
 * duration, weight, calories) did not survive: the next evaluateToday —
 * which runs on every PillarMissionCard and TacticalDashboard mount —
 * saw no entry, ran the detector, and put the check straight back. The
 * user could not override a mis-synced step count for more than a few
 * seconds.
 *
 * The fix has to record the un-check rather than erase it. It cannot
 * live inside `completed` as a `{ revoked: true }` entry, because EVERY
 * other reader of this doc tests key presence for truth —
 * computeDayOutcome, PillarMissionCard's row state, the calendar's day
 * breakdown, challengeInsights, computeWeeklyProgress. A tombstone there
 * is a truthy object, so a deliberately un-checked row would render as
 * DONE and count toward passing the day. A sibling map is invisible to
 * all of them: `completed[id]` stays absent, so every existing consumer
 * (and therefore tallyPillars + computeDayOutcome) already reads it as
 * NOT done, with no change outside this file.
 *
 * Scope is one day, by construction — the map lives on the day doc, so
 * an un-check never leaks into tomorrow.
 *
 * Not in the DailyStatus type (that lives in lib/pillarChallenges.ts);
 * read and written through this local cast, same as RolledOverStatus.
 */
type OverridableStatus = DailyStatus & {
  /** commitmentId → the moment the user un-checked it. `done` is always
   *  false today; the field is explicit so the record reads as a
   *  decision rather than as an empty marker. */
  manualOverrides?: Record<string, { at: string; done: false }>;
};

/** The un-check map off any day doc, never undefined. */
function overridesOf(status: OverridableStatus | DailyStatus | null | undefined): Record<string, { at: string; done: false }> {
  return { ...((status as OverridableStatus | null | undefined)?.manualOverrides ?? {}) };
}

/**
 * Build the Firestore payload for a day-status write.
 *
 * MERGE SEMANTICS — read this before changing anything here.
 *
 * Day docs are written by four independent paths: evaluateToday (auto-
 * detect), setCommitmentDone (manual tap), saveEvidenceToStatus (photo +
 * journal) and rolloverDay (lock-in). They all used to full-overwrite
 * the doc, so whichever wrote last destroyed the others' fields —
 * captured evidence vanished on the next auto-detect pass, and a
 * 'grace-used' stamp got recomputed back to 'failed'.
 *
 * Every write is now `{ merge: true }`. The cost of that is the flip
 * side: merge NEVER deletes a field. Un-checking a commitment, or
 * dropping one from the challenge, leaves a stale `completed[id]` key
 * behind forever — the row would silently stay checked. So removals are
 * explicit: callers pass the ids to drop and we write `deleteField()`
 * for each, which IS honoured inside a merging setDoc.
 *
 * The same delete-by-sentinel applies to `manualOverrides` (see
 * OverridableStatus): an un-check has to be prunable when the
 * commitment it belongs to leaves the challenge, and merge alone will
 * not clear it. `deleteField()` is honoured nested inside a merging
 * setDoc, which is exactly how `completed` keys are already removed.
 *
 * Also strips top-level `undefined` (Firestore rejects it) so callers
 * can spread optional fields like `evidence` without guarding.
 */
function buildDayWritePayload(
  status: OverridableStatus,
  opts: { removeCommitmentIds?: string[]; removeOverrideIds?: string[] } | undefined,
  fs: { serverTimestamp: () => unknown; deleteField: () => unknown },
): Record<string, unknown> {
  const completed: Record<string, unknown> = { ...status.completed };
  for (const id of opts?.removeCommitmentIds ?? []) completed[id] = fs.deleteField();
  const payload: Record<string, unknown> = { ...status, completed, updatedAt: fs.serverTimestamp() };

  // Only touch the overrides field when there is something to say about
  // it — writing an empty map on every pass would churn the doc for
  // every user who has never un-checked anything.
  const dropOverrides = opts?.removeOverrideIds ?? [];
  if (status.manualOverrides || dropOverrides.length > 0) {
    const manualOverrides: Record<string, unknown> = { ...(status.manualOverrides ?? {}) };
    for (const id of dropOverrides) manualOverrides[id] = fs.deleteField();
    payload.manualOverrides = manualOverrides;
  }

  for (const k of Object.keys(payload)) {
    if (payload[k] === undefined) delete payload[k];
  }
  return payload;
}

/**
 * Upsert a day's status document. Merging — see buildDayWritePayload.
 *
 * @param opts.removeCommitmentIds ids whose `completed` entry must be
 *        actively deleted (un-check, or a commitment removed/swapped out
 *        of the challenge). Merge alone will not clear them.
 * @param opts.removeOverrideIds ids whose `manualOverrides` entry must be
 *        actively deleted (the user re-checked it, or the commitment left
 *        the challenge).
 */
export async function saveDailyStatus(
  userId: string,
  challengeId: string,
  status: OverridableStatus,
  opts?: { removeCommitmentIds?: string[]; removeOverrideIds?: string[] },
): Promise<void> {
  const { doc, setDoc, serverTimestamp, deleteField } = await import('firebase/firestore');
  const { db } = await import('./firebase');
  const ref = doc(db, 'users', userId, COLLECTION, challengeId, 'days', status.date);
  await setDoc(
    ref,
    buildDayWritePayload(status, opts, { serverTimestamp, deleteField }) as any,
    { merge: true },
  );
}

/** Get the last N days of status for streak / calendar views. */
export async function getRecentDays(userId: string, challengeId: string, count = 14): Promise<DailyStatus[]> {
  try {
    const { collection, getDocs, query, orderBy, limit } = await import('firebase/firestore');
    const { db } = await import('./firebase');
    const ref = collection(db, 'users', userId, COLLECTION, challengeId, 'days');
    const q = query(ref, orderBy('date', 'desc'), limit(count));
    const snap = await getDocs(q);
    return snap.docs.map(d => d.data() as DailyStatus).reverse(); // oldest-first
  } catch (err) {
    console.warn('[challenges] getRecentDays failed:', err);
    return [];
  }
}

// ============================================================================
// AUTO-DETECTION — pull from existing OPMODE data sources
// ============================================================================

/**
 * Resolve the effective config for a commitment, applying user
 * overrides from the challenge's configOverrides map.
 */
export function effectiveConfig(commitment: Commitment, challenge: Pick<Challenge, 'configOverrides'>): Record<string, unknown> {
  return { ...commitment.config, ...(challenge.configOverrides[commitment.id] ?? {}) };
}

/**
 * Persist a config override for one commitment, merged onto whatever's
 * already there. Pass weeklyFrequency: null to clear it (back to daily).
 * Fires the challenge-changed event so the dashboard re-evaluates.
 */
export async function setCommitmentConfig(
  userId: string,
  challenge: Challenge,
  commitmentId: string,
  patch: Record<string, unknown>,
): Promise<Challenge> {
  const nextOverride: Record<string, unknown> = {
    ...(challenge.configOverrides?.[commitmentId] ?? {}),
    ...patch,
  };
  // Strip keys explicitly set to null/undefined so we don't persist junk.
  for (const k of Object.keys(nextOverride)) {
    if (nextOverride[k] === null || nextOverride[k] === undefined) delete nextOverride[k];
  }
  const configOverrides = { ...(challenge.configOverrides ?? {}), [commitmentId]: nextOverride };
  await updateChallenge(userId, challenge.id, { configOverrides });
  emitChallengeChanged();
  return { ...challenge, configOverrides } as Challenge;
}

/**
 * For a single commitment, check today's data sources and decide:
 *   - true: this commitment is auto-satisfied today
 *   - false: NOT satisfied today (data exists but doesn't meet threshold)
 *   - null: can't evaluate (manual-only commitment, or data not available)
 *
 * Returning null means the engine should defer to the manual completed[] map.
 */
export async function evaluateAutoDetect(
  userId: string,
  commitment: Commitment,
  challenge: Pick<Challenge, 'configOverrides'>,
  date: Date = new Date(),
  /** Optional: existing DailyStatus for the date. Some auto-detect
   *  cases (e.g. evidence-journal) need to read the current status doc;
   *  passing it through avoids a second Firestore read when the caller
   *  already has it loaded. */
  existingStatus?: { evidence?: { journal?: string } } | null,
): Promise<boolean | null> {
  const cfg = effectiveConfig(commitment, challenge);
  const dateStr = localDateString(date);

  switch (commitment.autoDetect) {
    // ── Manual types: never auto-evaluate.
    case 'manual-tap':
    case 'manual-confirm':
    case 'water-quantity':         // manual counter; engine reads counter elsewhere
    case 'time-outdoors':
    case 'screen-time-cutoff':
      return null;

    // ── Evidence journal: today's daily-status doc has non-empty
    //    evidence.journal text. The user writes once (in EvidenceCapture)
    //    and we credit both the evidence requirement AND the journal
    //    commitment from the same act.
    case 'evidence-journal': {
      const text = (existingStatus?.evidence?.journal ?? '').trim();
      return text.length > 0 ? true : null;
    }

    // ── Session duration: sum today's session durations.
    case 'session-duration': {
      const minutes = (cfg.minutes as number) ?? 45;
      const count = (cfg.count as number) ?? 1;
      const sessions = await getTodaySessions(userId, dateStr);
      if (sessions === null) return null; // store unreadable — abstain, don't deny
      const qualifying = sessions.filter(s => (s.duration ?? 0) >= minutes * 60);
      return qualifying.length >= count;
    }

    // ── Session by name pattern (yoga, ruck, etc.) + day-of-week gate.
    case 'session-type-match': {
      const regex = new RegExp((cfg.regex as string) ?? '', 'i');
      const minutes = (cfg.minutes as number) ?? 30;
      const dow = cfg.dayOfWeek as number | undefined; // 0=Sun
      if (dow !== undefined && date.getDay() !== dow) {
        // Not applicable today — return null so this doesn't count
        // against the user when it's not their day.
        return null;
      }
      const sessions = await getTodaySessions(userId, dateStr);
      if (sessions === null) return null; // store unreadable — abstain, don't deny
      return sessions.some(s => {
        const matches = s.exercises?.some((ex: any) => regex.test(ex.exerciseName));
        return matches && (s.duration ?? 0) >= minutes * 60;
      });
    }

    // ── Calorie under goal: sum today's nutrition vs goal.
    case 'calorie-under-goal': {
      const totals = await getTodayCalorieTotal(userId, dateStr);
      const goal = (cfg.useProfileGoal as boolean)
        ? await getUserCalorieGoal(userId)
        : (cfg.calorieGoal as number) ?? 2000;
      if (totals === null || goal === null || goal <= 0) return null;
      // Only auto-pass once total > 0 (user has logged something). If they
      // logged nothing all day, we don't know — could be no eating, could
      // be no logging. Stay pending until user manually confirms or rollover.
      if (totals === 0) return null;
      return totals <= goal;
    }

    // ── Step goal: dailyActivity.steps >= threshold.
    case 'steps-goal': {
      const activity = await getTodayActivity(userId, dateStr);
      if (!activity || activity.steps == null) return null;
      const threshold = (cfg.steps as number) ?? 10000;
      return activity.steps >= threshold;
    }

    // ── Sleep minutes goal: dailyActivity.sleepDurationMinutes >= threshold.
    case 'sleep-minutes-goal': {
      const activity = await getTodayActivity(userId, dateStr);
      if (!activity || activity.sleepDurationMinutes == null) return null;
      const threshold = (cfg.minutes as number) ?? 420;
      return activity.sleepDurationMinutes >= threshold;
    }

    // ── Wake by time: first foreground event before threshold.
    case 'wake-by-time': {
      const threshold = (cfg.time as string) ?? '06:30';
      const wakeAt = getFirstWakeTimestamp(dateStr);
      if (!wakeAt) return null; // can't determine yet
      const [h, m] = threshold.split(':').map(Number);
      const thresholdMs = (h * 60 + m) * 60_000;
      const wakeDate = new Date(wakeAt);
      const wakeMs = (wakeDate.getHours() * 60 + wakeDate.getMinutes()) * 60_000;
      return wakeMs <= thresholdMs;
    }

    // ── Weight logged today.
    case 'weight-logged': {
      const activity = await getTodayActivity(userId, dateStr);
      if (!activity) return null;
      return (activity.weight ?? 0) > 0;
    }

    default:
      return null;
  }
}

// ============================================================================
// DATA SOURCE ADAPTERS — wrap existing OPMODE APIs with the bits we need
// ============================================================================

/**
 * Sessions logged within the local calendar day `dateStr`.
 *
 * Returns null when the session store could not be read at all. That is
 * NOT the same as "no sessions" — callers must abstain (return null)
 * rather than reporting a miss, otherwise a transient read failure
 * revokes a legitimately auto-detected workout.
 *
 * The window used to be `new Date(dateStr)`, which parses as UTC
 * midnight and therefore lands on the PREVIOUS local day everywhere west
 * of Greenwich. That is why workout commitments silently failed for
 * every user in the Americas: a session logged at 09:00 local was
 * compared against yesterday's window.
 */
async function getTodaySessions(
  userId: string,
  dateStr: string,
): Promise<Array<{ duration?: number; exercises?: any[] }> | null> {
  try {
    const { getSessionHistory } = await import('./sessionStorage');
    const { start, end } = localDayWindow(dateStr);
    const sessions = await getSessionHistory({ since: start });
    return sessions.filter(s => {
      const d = new Date(s.date);
      return d >= start && d < end;
    }) as any;
  } catch (err) {
    console.warn('[challenges] getTodaySessions failed:', err);
    return null;
  }
}

async function getTodayCalorieTotal(userId: string, dateStr: string): Promise<number | null> {
  try {
    const { getNutritionEntries } = await import('./db');
    const entries = await getNutritionEntries(userId, dateStr);
    return entries.reduce((sum, e) => sum + (e.calories ?? 0), 0);
  } catch (err) {
    console.warn('[challenges] getTodayCalorieTotal failed:', err);
    return null;
  }
}

async function getUserCalorieGoal(userId: string): Promise<number | null> {
  try {
    const { getNutritionGoals } = await import('./db');
    const goals = await getNutritionGoals(userId);
    return goals?.calories ?? null;
  } catch (err) {
    console.warn('[challenges] getUserCalorieGoal failed:', err);
    return null;
  }
}

/**
 * The wearable/NEAT activity doc for the local calendar day `dateStr`.
 *
 * DATED READ, not a "today" read. `dailyActivity` entries are keyed
 * `${userId}-${YYYY-MM-DD}` on the LOCAL date, so an exact keyed get for
 * any past day is available — no guard/abstain fallback needed. Before
 * this, evaluating yesterday (midnight rollover, catch-up) read TODAY's
 * steps and sleep and credited or denied yesterday on them.
 */
async function getTodayActivity(userId: string, dateStr: string): Promise<{ steps?: number; sleepDurationMinutes?: number; weight?: number } | null> {
  try {
    const { getTodayActivity: g } = await import('./db');
    return (await g(userId, dateStr)) ?? null;
  } catch (err) {
    console.warn('[challenges] getTodayActivity failed:', err);
    return null;
  }
}

// ── Wake-time detection — first app-foreground event of the day ─────────────
//
// We stamp into localStorage when the app first becomes active each
// calendar day. The Capacitor App appStateChange listener in
// AuthContext writes here; the challenge engine reads it.

const WAKE_KEY = 'opmode:firstWakeAt';

/** Stamp the first foreground event of today. No-op if already stamped. */
export function stampWake(now: Date = new Date()): void {
  if (typeof window === 'undefined') return;
  try {
    const dateStr = localDateString(now);
    const raw = localStorage.getItem(WAKE_KEY);
    const map: Record<string, string> = raw ? JSON.parse(raw) : {};
    if (!map[dateStr]) {
      map[dateStr] = now.toISOString();
      localStorage.setItem(WAKE_KEY, JSON.stringify(map));
    }
  } catch { /* non-critical */ }
}

/** Get the first wake-up timestamp for a given local date, if any. */
function getFirstWakeTimestamp(dateStr: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(WAKE_KEY);
    if (!raw) return null;
    const map = JSON.parse(raw) as Record<string, string>;
    return map[dateStr] ?? null;
  } catch { return null; }
}

// ============================================================================
// FULL DAY EVALUATION
// ============================================================================

/** Every commitment id currently configured on the challenge, across
 *  all three pillars. Uses the RAW id lists (not challengeCommitments)
 *  so an id the library no longer knows about is still treated as
 *  "configured" and never silently pruned from the day doc. */
function liveCommitmentIds(challenge: Pick<Challenge, 'commitments'>): Set<string> {
  return new Set([
    ...challenge.commitments.physical,
    ...challenge.commitments.mental,
    ...challenge.commitments.spiritual,
  ]);
}

/**
 * Per-pillar done/total tally over a completed map, for the day `date`.
 *
 * ONLY commitments required on that date are counted — both in `total`
 * and in `done`. A 3×/week workout or a Sunday-only yoga slot is not a
 * miss on a day it doesn't apply to, and counting it dragged every such
 * pillar to "2/3" on a day the user had actually done everything owed.
 * Counting a non-required completion in `done` but not `total` would
 * produce 4/3, so both sides use the same filter.
 */
function tallyPillars(
  challenge: Pick<Challenge, 'commitments' | 'configOverrides'>,
  completed: DailyStatus['completed'],
  date: Date,
) {
  const tally = (ids: string[]) => {
    const required = ids.filter(id => isDailyRequired(id, challenge, date));
    return {
      done: required.filter(id => completed[id]).length,
      total: required.length,
    };
  };
  return {
    physical: tally(challenge.commitments.physical),
    mental: tally(challenge.commitments.mental),
    spiritual: tally(challenge.commitments.spiritual),
  };
}

/**
 * True when a freshly-computed status carries no new information vs the
 * stored one, so the write can be skipped entirely. Deliberately ignores
 * `updatedAt` (it changes on every run by construction) and the `at`
 * timestamp inside each completion (only the presence of the key and the
 * auto/manual flag are meaningful).
 */
function dayStatusEquivalent(a: OverridableStatus, b: OverridableStatus): boolean {
  if (a.dayNumber !== b.dayNumber || a.outcome !== b.outcome) return false;
  // An un-check is state too — a pass that adds or drops one must write.
  const oa = Object.keys(a.manualOverrides ?? {});
  const ob = Object.keys(b.manualOverrides ?? {});
  if (oa.length !== ob.length || oa.some(k => !(b.manualOverrides ?? {})[k])) return false;
  const pillars = ['physical', 'mental', 'spiritual'] as const;
  for (const p of pillars) {
    if (a.pillarCounts?.[p]?.done !== b.pillarCounts?.[p]?.done) return false;
    if (a.pillarCounts?.[p]?.total !== b.pillarCounts?.[p]?.total) return false;
  }
  const ka = Object.keys(a.completed ?? {});
  const kb = Object.keys(b.completed ?? {});
  if (ka.length !== kb.length) return false;
  for (const k of ka) {
    const ea = a.completed[k];
    const eb = b.completed[k];
    if (!eb) return false;
    if (ea.auto !== eb.auto || ea.note !== eb.note) return false;
  }
  return true;
}

/**
 * Evaluate today's status for a challenge: run all auto-detectors,
 * merge with any existing manual checks from Firestore, recompute
 * pillar counts + outcome, persist.
 *
 * Called:
 *   - On dashboard mount (cheap re-eval to keep card fresh)
 *   - On manual check toggle
 *   - At midnight rollover (final lock-in)
 *
 * Idempotent — running it twice in a row produces the same result, and
 * the second run doesn't write at all.
 *
 * THREE THINGS THIS MUST NOT DO:
 *   1. Destroy evidence. The day write merges now, but the returned
 *      status is what the UI renders, so any existing evidence is
 *      carried through explicitly.
 *   2. Downgrade a terminal outcome. A 'grace-used' day was PAID for
 *      with a finite resource, and a 'passed' day is banked. Neither is
 *      recomputed here — auto-detection re-running (say, from a
 *      dashboard mount days later) must never turn a banked day back
 *      into pending/failed.
 *   3. Leave stale keys behind. Merge writes don't clear removed
 *      fields, so a commitment that's been swapped or dropped out of
 *      the challenge has its `completed` entry deleted explicitly.
 */
export async function evaluateToday(
  userId: string,
  challenge: Challenge,
  date: Date = new Date()
): Promise<DailyStatus> {
  const dateStr = localDateString(date);
  const dayNum = dayNumberFor(challenge, date);
  const commitments = challengeCommitments(challenge);

  // Load existing status to preserve manual checks (those don't auto-revert).
  const existing = (await getDailyStatus(userId, challenge.id, dateStr)) as OverridableStatus | null;
  const completed = { ...(existing?.completed ?? {}) };
  // Commitments the user explicitly un-checked today (see OverridableStatus).
  const overrides = overridesOf(existing);

  // Stale keys: commitments that were completed on this day but are no
  // longer part of the challenge (swapped out, or removed by an edit).
  // With merge writes these survive forever unless deleted explicitly,
  // and they'd keep inflating "how much did I do" reads.
  //
  // An un-check for a REMOVED commitment is pruned for the same reason —
  // it protects nothing once the commitment is gone. An un-check for a
  // commitment that is still live MUST survive this, or the un-check
  // dies on the very next pass, which is the bug it exists to fix.
  const liveIds = liveCommitmentIds(challenge);
  const staleIds = Object.keys(completed).filter(id => !liveIds.has(id));
  for (const id of staleIds) delete completed[id];
  const staleOverrideIds = Object.keys(overrides).filter(id => !liveIds.has(id));
  for (const id of staleOverrideIds) delete overrides[id];

  // A detection is a CLAIM ABOUT THE DATA, so it has to be able to move
  // in both directions. This loop used to `continue` on any existing
  // entry, which made every auto-check permanent: a 300 kcal breakfast
  // satisfied "stay under your calorie goal" for the rest of the day no
  // matter what got logged after it.
  //
  // Revocation is fenced in tightly, because un-checking something is the
  // one operation here that can move a day toward failing:
  //   - Manual checks are NEVER revoked. `auto === false` is explicit user
  //     intent and the system does not get a vote on it. (Legacy entries
  //     with no `auto` flag are treated as manual — the safe reading.)
  //   - An un-check is user intent too, and is equally never overridden.
  //     It is recorded in `manualOverrides` rather than by deleting the
  //     key, because a deleted key reads as "never detected" and the
  //     detector below would simply re-add it seconds later.
  //   - Only the CURRENT local day is revocable. A past day is evaluated
  //     from data that may simply not be on this device (Dexie is local;
  //     a workout logged on another phone isn't here), and abstaining is
  //     the only honest answer. This also keeps midnight rollover and any
  //     catch-up pass from retroactively un-checking history.
  //   - A terminal day (passed / grace-used) is banked. Its counts stay
  //     as they were locked in.
  //   - `null` from a detector means "can't tell" and always abstains.
  //     Only an explicit `false` — data present, threshold not met —
  //     revokes.
  //
  // Pass `existing` so the evidence-journal detector can read the
  // current evidence subdoc without a second Firestore round-trip.
  const isTerminalDay = existing?.outcome === 'passed' || existing?.outcome === 'grace-used';
  const revokable = dateStr === localDateString() && !isTerminalDay;
  const revokedIds: string[] = [];
  const clearedOverrideIds: string[] = [];

  for (const c of commitments) {
    const entry = completed[c.id];
    const override = overrides[c.id];

    if (override) {
      // The user un-checked this today. Normally there is no `completed`
      // entry left to argue with — setCommitmentDone deleted it — so we
      // just refuse to run the detector and the row stays off.
      //
      // An entry CAN reappear from another device that auto-detected
      // after our un-check synced. Resolve by timestamp: only a check
      // recorded strictly after the un-check supersedes it.
      if (!entry || !entry.at || entry.at <= override.at) {
        if (entry) { delete completed[c.id]; revokedIds.push(c.id); }
        continue;
      }
      // A later check wins — the un-check is spent, drop it so it can't
      // haunt the rest of the day.
      delete overrides[c.id];
      clearedOverrideIds.push(c.id);
    }

    if (entry && entry.auto !== true) continue; // manual (or legacy) — user's call, not ours
    if (entry && !revokable) continue;          // auto-done, but not re-checkable today

    const auto = await evaluateAutoDetect(userId, c, challenge, date, existing);
    if (auto === true) {
      // Keep the original timestamp when it was already detected — the
      // day's `at` should record when it was first satisfied, and
      // rewriting it every pass churns the doc for no information.
      if (!entry) completed[c.id] = { at: new Date().toISOString(), auto: true };
    } else if (auto === false && entry) {
      delete completed[c.id];
      revokedIds.push(c.id);
    }
  }

  // Merge writes never clear a field, so a revoked entry has to be
  // deleted explicitly alongside the stale ones (see buildDayWritePayload).
  const removeIds = [...staleIds, ...revokedIds];
  const removeOverrideIds = [...staleOverrideIds, ...clearedOverrideIds];

  const pillarCounts = tallyPillars(challenge, completed, date);

  // Determine outcome — but never over the top of a terminal one.
  const computed = computeDayOutcome({ pillarCounts, completed }, challenge, date);
  const terminal =
    existing?.outcome === 'grace-used' || existing?.outcome === 'passed'
      ? existing.outcome
      : null;
  const outcome: DailyStatus['outcome'] = terminal ?? computed;

  const status: OverridableStatus = {
    date: dateStr,
    dayNumber: dayNum,
    completed,
    pillarCounts,
    outcome,
    updatedAt: new Date().toISOString(),
    // Carry evidence through: it belongs to the day, not to this pass.
    ...(existing?.evidence ? { evidence: existing.evidence } : {}),
    // Carry un-checks through for the same reason — dropping them here
    // would let the next pass re-detect what the user turned off.
    ...(Object.keys(overrides).length > 0 ? { manualOverrides: overrides } : {}),
  };

  // Nothing changed — don't write, don't churn the notification queue.
  if (
    existing &&
    removeIds.length === 0 &&
    removeOverrideIds.length === 0 &&
    dayStatusEquivalent(existing, status)
  ) {
    return { ...status, updatedAt: existing.updatedAt };
  }

  await saveDailyStatus(userId, challenge.id, status, {
    removeCommitmentIds: removeIds,
    removeOverrideIds,
  });

  // Refresh local notifications so the evening/final-call reminders
  // reflect current open-item count. Idempotent — cancels then
  // re-schedules. Skipped silently on web.
  try {
    const { scheduleChallengeNotifications } = await import('./pillarChallengeNotifications');
    void scheduleChallengeNotifications(challenge, status);
  } catch (e) {
    console.warn('[challenges] notification refresh failed:', e);
  }

  return status;
}

/**
 * Manually mark a commitment as complete (or undo).
 *
 * TRANSACTIONAL. A checkbox tap and an evaluateToday auto-detect pass
 * routinely overlap (the dashboard re-evaluates on mount and on the
 * challenge-changed event). Read-modify-write outside a transaction let
 * whichever finished last win with a snapshot taken before the other
 * started — so a tap could be flipped straight back. The read and the
 * write now commit atomically; Firestore retries the callback on
 * contention.
 *
 * An un-check writes `deleteField()` for the key, because a merging
 * write cannot clear it (see buildDayWritePayload) — AND records the
 * un-check in `manualOverrides`, because deleting the key alone is
 * indistinguishable from "never detected" and the next evaluateToday
 * pass would re-detect it (see OverridableStatus).
 *
 * On a BANKED day the completed map still changes honestly, but the
 * day's outcome does not move. See the terminal guard in build().
 */
export async function setCommitmentDone(
  userId: string,
  challenge: Challenge,
  commitmentId: string,
  done: boolean,
  date: Date = new Date(),
  /** Optional free-text payload — gratitude list, prayer notes, etc.
   *  Persisted under completed[commitmentId].note for later review. */
  note?: string,
): Promise<DailyStatus> {
  const dateStr = localDateString(date);

  /** The challenge as the transaction actually saw it — used for the
   *  notification refresh below so it can't reschedule off a commitment
   *  list the swap already replaced. Assigned by every build() run; the
   *  committed attempt is the last one Firestore executes. */
  let effective: Challenge = challenge;

  /** Pure: stored status + stored challenge in → status to write + keys
   *  to delete. Runs inside the transaction (and again in the fallback
   *  below), so it must not do IO.
   *
   *  WHY THE CHALLENGE IS A PARAMETER AND NOT THE CAPTURED PROP:
   *  `liveIds` drives `remove`, which becomes deleteField() calls. A
   *  surface holding a PRE-SWAP challenge (TodayChecklist is mounted on
   *  the dashboard card AND on /challenge at the same time, so one copy
   *  routinely goes stale the moment the other swaps) would compute
   *  liveIds without the swapped-IN commitment and delete its completion
   *  — a write that looks like a normal prune and is unrecoverable. The
   *  transaction fixed lost updates on the `completed` map's own keys;
   *  it could not fix this, because the stale input was the commitment
   *  list, not the day doc. So the caller's copy is only a fallback for
   *  when the challenge doc can't be read at all. Same reasoning applies
   *  to pillarCounts/outcome (tallied against the wrong list) and to
   *  dayNumber (stale startDate after a restart). */
  const build = (
    existing: OverridableStatus | null,
    storedChallenge: Challenge | null,
  ): { status: OverridableStatus; remove: string[]; removeOverrides: string[] } => {
    // Spread over the caller's copy rather than replacing it: the stored
    // doc is authoritative for everything it carries, and anything a
    // legacy doc is missing still resolves.
    const ch: Challenge = storedChallenge
      ? ({ ...challenge, ...storedChallenge, id: challenge.id } as Challenge)
      : challenge;
    effective = ch;
    const liveIds = liveCommitmentIds(ch);
    const dayNum = dayNumberFor(ch, date);

    const completed = { ...(existing?.completed ?? {}) };
    const overrides = overridesOf(existing);
    // Prune commitments no longer on the challenge, same as evaluateToday.
    // The tapped id is exempt: with liveIds now coming from the STORED
    // challenge it can itself be non-live (a tap on a surface whose row
    // was swapped OUT elsewhere). Writing that key is inert — tallyPillars
    // and computeDayOutcome both count only live ids, so it moves no
    // counter — and the next evaluateToday prunes it. Deleting it in the
    // same write that adds it would just be self-contradictory.
    const remove = Object.keys(completed).filter(id => !liveIds.has(id) && !(done && id === commitmentId));
    for (const id of remove) delete completed[id];
    // An un-check for a commitment that has left the challenge protects
    // nothing — prune it. One for a live commitment must survive.
    const removeOverrides = Object.keys(overrides).filter(id => !liveIds.has(id));
    for (const id of removeOverrides) delete overrides[id];

    if (done) {
      completed[commitmentId] = {
        at: new Date().toISOString(),
        auto: false,
        ...(note && note.trim() ? { note: note.trim().slice(0, 500) } : {}),
      };
      // Re-checking retracts the un-check.
      if (overrides[commitmentId]) {
        delete overrides[commitmentId];
        if (!removeOverrides.includes(commitmentId)) removeOverrides.push(commitmentId);
      }
    } else {
      delete completed[commitmentId];
      if (!remove.includes(commitmentId)) remove.push(commitmentId);
      // Record the un-check. Without this the deletion above is undone
      // by the next auto-detect pass on any detectable commitment.
      overrides[commitmentId] = { at: new Date().toISOString(), done: false };
      const idx = removeOverrides.indexOf(commitmentId);
      if (idx >= 0) removeOverrides.splice(idx, 1);
    }

    const pillarCounts = tallyPillars(ch, completed, date);
    const computed = computeDayOutcome({ pillarCounts, completed }, ch, date);
    // ── BANKED-DAY GUARD ────────────────────────────────────────────
    // A grace-used day was paid for out of a finite pool. A day carrying
    // `rolloverAppliedAt` has already had its streak increment, its
    // totalDaysCompleted and its longestStreak folded into the challenge
    // by rolloverSingleDay, in one transaction with that stamp.
    //
    // Recomputing either one here writes an outcome the counters no
    // longer agree with, and nothing ever reconciles them: the stamp
    // stays, so no future rollover will re-settle the day, while the
    // streak it paid out stays banked. The calendar then draws a failed
    // day sitting inside an unbroken streak.
    //
    // So the guard is symmetric with evaluateToday's. The completed map
    // and pillar counts above still record what the user actually did —
    // we refuse to move the VERDICT, not to record the fact.
    const banked =
      existing?.outcome === 'grace-used' ||
      !!(existing as RolledOverStatus | null)?.rolloverAppliedAt;
    const outcome: DailyStatus['outcome'] =
      banked && existing ? existing.outcome : computed;

    return {
      status: {
        date: dateStr,
        dayNumber: dayNum,
        completed,
        pillarCounts,
        outcome,
        updatedAt: new Date().toISOString(),
        ...(existing?.evidence ? { evidence: existing.evidence } : {}),
        ...(Object.keys(overrides).length > 0 ? { manualOverrides: overrides } : {}),
      },
      remove,
      removeOverrides,
    };
  };

  const { doc, getDoc, runTransaction, serverTimestamp, deleteField } = await import('firebase/firestore');
  const { db } = await import('./firebase');
  const ref = doc(db, 'users', userId, COLLECTION, challenge.id, 'days', dateStr);
  const challengeRef = doc(db, 'users', userId, COLLECTION, challenge.id);

  let status: DailyStatus;
  try {
    status = await runTransaction(db, async tx => {
      // Both reads first — Firestore forbids a read after a write in a
      // transaction. The challenge read is what makes the commitment
      // list current; contention on either doc retries the callback.
      const chSnap = await tx.get(challengeRef);
      const snap = await tx.get(ref);
      const stored = chSnap.exists() ? (chSnap.data() as Challenge) : null;
      const existing = snap.exists() ? (snap.data() as OverridableStatus) : null;
      const { status: next, remove, removeOverrides } = build(existing, stored);
      tx.set(
        ref,
        buildDayWritePayload(
          next,
          { removeCommitmentIds: remove, removeOverrideIds: removeOverrides },
          { serverTimestamp, deleteField },
        ) as any,
        { merge: true },
      );
      return next;
    });
  } catch (err) {
    // Transactions require a live connection — offline they reject
    // rather than queue. Falling back to the plain merging write keeps
    // the tap working on a bad connection; it is exactly the old
    // behaviour, so it is never worse than what shipped before.
    console.warn('[challenges] setCommitmentDone transaction failed, falling back:', err);
    const existing = (await getDailyStatus(userId, challenge.id, dateStr)) as OverridableStatus | null;
    // Still prefer the STORED commitment list here. Offline this get is
    // served from the local cache, which is at worst as stale as the
    // caller's prop and usually fresher; if it throws or the doc is
    // gone, build() falls back to the caller's copy on its own.
    let stored: Challenge | null = null;
    try {
      const chSnap = await getDoc(challengeRef);
      if (chSnap.exists()) stored = chSnap.data() as Challenge;
    } catch (e) {
      console.warn('[challenges] setCommitmentDone fallback challenge read failed:', e);
    }
    const { status: next, remove, removeOverrides } = build(existing, stored);
    await saveDailyStatus(userId, challenge.id, next, {
      removeCommitmentIds: remove,
      removeOverrideIds: removeOverrides,
    });
    status = next;
  }

  // Manual check changes open-count → refresh evening / final-call
  // schedule. When the user just checked their last item, this cancels
  // both reminders (so we don't ping a fully-locked-in user at 9 PM).
  try {
    const { scheduleChallengeNotifications } = await import('./pillarChallengeNotifications');
    // `effective`, not the caller's prop — the open-item count has to be
    // read off the same commitment list the write was computed against.
    void scheduleChallengeNotifications(effective, status);
  } catch {}
  return status;
}

// ============================================================================
// MIDNIGHT ROLLOVER
// ============================================================================

/**
 * Run at the start of each new day. Evaluates yesterday's final status
 * (locking in pass/fail), updates challenge counters, and surfaces the
 * restart modal if needed.
 *
 * Returns a `RolloverResult` so callers can show the right UI.
 */
export interface RolloverResult {
  challengeId: string;
  /** The day that just ended. */
  yesterdayDate: string;
  yesterdayDayNumber: number;
  yesterdayOutcome: 'passed' | 'failed' | 'grace-used';
  /** True when failed and the user should be prompted to restart or use grace. */
  needsRestartDecision: boolean;
  /** Updated challenge after rollover (counters incremented or reset). */
  updatedChallenge: Challenge;
  /**
   * False when the day's outcome was computed but the write that banks it
   * did NOT land (a swallowed transaction failure).
   *
   * WHY THIS EXISTS: without it, a passed day whose bank transaction threw
   * still returned a success-shaped result, and catchUpRollover advanced the
   * watermark past it unconditionally. Because the next walk starts at
   * watermark + 1, that day was never revisited — the streak increment was
   * lost permanently and the day was silently forgiven. The walk must not
   * advance past a day it did not actually settle.
   */
  settled: boolean;
}

/**
 * A day doc, plus the marker rolloverDay stamps once it has folded that
 * day into the challenge counters.
 *
 * WHY THIS EXISTS: 'passed' on the day doc says the day was WON, not
 * that its streak increment has been banked. Without a separate marker,
 * every rolloverDay call on the same day re-incremented currentStreak —
 * which is how a re-firing RestartGate double-counted an already-graced
 * or already-passed day. The stamp is written in the SAME transaction as
 * the counter update, so the two can't disagree.
 *
 * Not in the DailyStatus type (that lives in lib/pillarChallenges.ts);
 * read and written through this local cast.
 */
type RolledOverStatus = DailyStatus & { rolloverAppliedAt?: string };

export async function rolloverDay(
  userId: string,
  challenge: Challenge,
  asOf: Date = new Date()
): Promise<RolloverResult | null> {
  const yesterday = new Date(asOf);
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = localDateString(yesterday);

  // Honour the rollover watermark when the challenge carries one: any day
  // at or before `lastRolloverDate` has already been accounted for and
  // must never be re-litigated. Pre-existing challenges are seeded with
  // yesterday (see catchUpRollover), so a user on day 70 never gets 70
  // days re-evaluated. Ordinary string compare is safe — these are
  // zero-padded YYYY-MM-DD.
  const watermark = challenge.lastRolloverDate;
  if (watermark && yesterdayStr <= watermark) return null;

  return rolloverSingleDay(userId, challenge, yesterdayStr);
}

/**
 * Roll over ONE named local date. This is the whole of the old
 * `rolloverDay` body with the date lifted out of it, so `rolloverDay`
 * (RestartGate's existing call, unchanged) and `catchUpRollover` (the
 * walk) share exactly one implementation of "settle a day".
 *
 * Applies NO watermark check — the caller owns the range.
 *
 * Returns null when there is no day doc for `dateStr`. A day the user
 * never opened the app on is FORGIVEN, not failed: we have no record of
 * what they did, and manufacturing a failure from an absence of data is
 * how you retroactively kill a 70-day streak.
 */
async function rolloverSingleDay(
  userId: string,
  challenge: Challenge,
  yesterdayStr: string,
): Promise<RolloverResult | null> {
  const yesterday = parseLocalDate(yesterdayStr);

  const stored = (await getDailyStatus(userId, challenge.id, yesterdayStr)) as RolledOverStatus | null;
  if (!stored) return null; // no record for that day — nothing to roll over

  const settled = (outcome: 'passed' | 'grace-used', updated: Challenge = challenge): RolloverResult => ({
    challengeId: challenge.id,
    yesterdayDate: yesterdayStr,
    yesterdayDayNumber: stored.dayNumber,
    yesterdayOutcome: outcome,
    needsRestartDecision: false,
    updatedChallenge: updated,
    settled: true,
  });

  // ── TERMINAL-DAY GUARD ────────────────────────────────────────────
  // A day that already resolved is never re-evaluated and never
  // re-counted. Re-evaluating it is what let the restart gate fire again
  // on a day the user had already paid a grace day for, and recompute it
  // straight back to 'failed'.
  //
  // 'grace-used' is terminal AND already counted: useGraceDay does its
  // own currentStreak increment, so rolloverDay must never add a second.
  if (stored.outcome === 'grace-used') return settled('grace-used');
  // 'passed' is terminal, but only already-counted once the stamp is on it.
  if (stored.outcome === 'passed' && stored.rolloverAppliedAt) return settled('passed');

  // Only a genuinely unresolved day gets re-evaluated (in case the
  // auto-detectors never ran at midnight). A stored 'passed' that hasn't
  // been counted yet is taken as-is — it's terminal, so there is nothing
  // to recompute, only counters to bank.
  const finalStatus =
    stored.outcome === 'passed' ? stored : await evaluateToday(userId, challenge, yesterday);

  // Defensive: evaluateToday preserves terminal outcomes, so this can
  // only be reached if the doc changed underneath us.
  if (finalStatus.outcome === 'grace-used') return settled('grace-used');

  // Strict mode: any missed commitment with no manual completion = fail
  // pillar-collapse: any pillar with zero completions = fail
  const outcome: 'passed' | 'failed' = finalStatus.outcome === 'passed' ? 'passed' : 'failed';

  const { doc, runTransaction, serverTimestamp } = await import('firebase/firestore');
  const { db } = await import('./firebase');
  const challengeRef = doc(db, 'users', userId, COLLECTION, challenge.id);
  const dayRef = doc(db, 'users', userId, COLLECTION, challenge.id, 'days', yesterdayStr);

  let updatedChallenge: Challenge = { ...challenge };
  let statusChanged = false;
  let applied = false;

  if (outcome === 'passed') {
    // Bank the day. Everything is read fresh inside the transaction:
    // the caller's `challenge` may be minutes old (RestartGate reads it
    // once on mount and the modal can sit open indefinitely), and
    // incrementing off a stale counter is how streaks drift.
    try {
      await runTransaction(db, async tx => {
        // Firestore re-runs this callback on contention — reset every
        // outer flag so a discarded attempt can't leak into the result.
        statusChanged = false;
        applied = false;
        const chSnap = await tx.get(challengeRef);
        if (!chSnap.exists()) return;
        const cur = chSnap.data() as Challenge;
        updatedChallenge = { ...cur };
        // Only an arc that is still live is ours to advance. 'paused' and
        // 'completed' belong to other flows; 'failed' is revived by a
        // passing day (the late-check-in recovery path).
        if (cur.status !== 'active' && cur.status !== 'failed') return;
        // Re-read the stamp inside the transaction: another device may
        // have banked this day between our read and this write.
        const daySnap = await tx.get(dayRef);
        if ((daySnap.data() as RolledOverStatus | undefined)?.rolloverAppliedAt) return;

        const currentStreak = (cur.currentStreak ?? 0) + 1;
        const totalDaysCompleted = (cur.totalDaysCompleted ?? 0) + 1;
        const longestStreak = Math.max(cur.longestStreak ?? 0, currentStreak);
        const finished = currentStreak >= cur.duration;
        const nextStatus: Challenge['status'] = finished ? 'completed' : 'active';
        const completedAt = finished ? new Date().toISOString() : cur.completedAt;

        tx.update(challengeRef, {
          currentStreak,
          totalDaysCompleted,
          longestStreak,
          status: nextStatus,
          ...(completedAt ? { completedAt } : {}),
          updatedAt: serverTimestamp(),
        } as any);
        // Same transaction: outcome locked in + the day marked as banked.
        tx.set(
          dayRef,
          { outcome: 'passed', rolloverAppliedAt: new Date().toISOString(), updatedAt: serverTimestamp() } as any,
          { merge: true },
        );

        statusChanged = nextStatus !== cur.status;
        applied = true;
        updatedChallenge = {
          ...cur,
          currentStreak,
          totalDaysCompleted,
          longestStreak,
          status: nextStatus,
          ...(completedAt ? { completedAt } : {}),
        };
      });
      if (applied) emitChallengeChanged();
    } catch (e) {
      // Nothing was written, and nothing was stamped — the next rollover
      // pass will retry cleanly. Better than half-applying counters.
      console.warn('[challenges] passed-day rollover failed:', e);
    }
  } else {
    // Failed. The caller still decides HOW to respond (grace / restart /
    // pause / late check-in) — we don't reset counters here.
    //
    // But the failure itself has to be DURABLE. Before this, the only
    // record was `needsRestartDecision` on the in-memory result, so the
    // challenge doc stayed status:'active' forever and nothing — not the
    // /challenge failed screen, not the status footer — could ever tell
    // a dead arc from a live one. Persist it. Recovery paths
    // (useGraceDay, restartChallenge, a later passing rollover) all put
    // the status back to 'active'.
    //
    // Only an 'active' arc is ours to kill, and that check reads the
    // STORED status inside the transaction — not the caller's snapshot.
    // RestartGate loads the challenge once on mount and the modal can
    // stay open for hours; if the arc gets paused or ended from another
    // device in the meantime, gating on the stale copy would clobber it
    // back to 'failed'.
    try {
      await runTransaction(db, async tx => {
        statusChanged = false; // reset per retry attempt
        const chSnap = await tx.get(challengeRef);
        if (!chSnap.exists()) return;
        const cur = chSnap.data() as Challenge;
        updatedChallenge = { ...cur };
        // The DAY's verdict is recorded for any live arc, even one that
        // is already 'failed' from an earlier pass — otherwise the day
        // doc stays 'pending' forever and the calendar can't tell a
        // missed day from a day that simply hasn't happened yet.
        // Re-read it inside the transaction first: another device may
        // have graced or passed it since our read, and those are
        // terminal.
        if (cur.status === 'active' || cur.status === 'failed') {
          const daySnap = await tx.get(dayRef);
          const dayOutcome = (daySnap.data() as DailyStatus | undefined)?.outcome;
          if (dayOutcome !== 'grace-used' && dayOutcome !== 'passed') {
            tx.set(dayRef, { outcome: 'failed', updatedAt: serverTimestamp() } as any, { merge: true });
          }
        }
        if (cur.status !== 'active') return;
        tx.update(challengeRef, { status: 'failed', updatedAt: serverTimestamp() } as any);
        updatedChallenge = { ...cur, status: 'failed' };
        statusChanged = true;
      });
      if (statusChanged) emitChallengeChanged();
    } catch (e) {
      // A write failure must not swallow the decision — the caller
      // still gets needsRestartDecision and the gate still fires,
      // and updatedChallenge keeps the pre-rollover status.
      console.warn('[challenges] failed-status persist failed:', e);
    }
  }

  // updateChallenge() cancels pending reminders on any status change;
  // the raw transactions above bypass it, so mirror that here.
  if (statusChanged) {
    try {
      const { cancelChallengeNotifications } = await import('./pillarChallengeNotifications');
      void cancelChallengeNotifications();
    } catch {}
  }

  return {
    challengeId: challenge.id,
    yesterdayDate: yesterdayStr,
    yesterdayDayNumber: finalStatus.dayNumber,
    yesterdayOutcome: outcome,
    needsRestartDecision: outcome === 'failed',
    updatedChallenge,
    // A passed day is only settled once its bank transaction actually landed.
    // A failed day is settled by the day doc evaluateToday already wrote; the
    // status flip is a separate concern and the walk parks on it either way.
    settled: outcome === 'passed' ? applied : true,
  };
}

/** `dateStr` shifted by `n` local days, back as YYYY-MM-DD. */
function addLocalDays(dateStr: string, n: number): string {
  const d = parseLocalDate(dateStr);
  d.setDate(d.getDate() + n);
  return localDateString(d);
}

export interface CatchUpResult {
  /** The challenge after every day in the range was folded in. */
  challenge: Challenge;
  /** One entry per day that actually settled, oldest first. */
  days: RolloverResult[];
  /** The failed day the user still owes a decision on (grace / restart),
   *  if the walk stopped on one. This is what should drive the restart
   *  gate — it is the catch-up equivalent of rolloverDay's
   *  `needsRestartDecision`. */
  pendingDecision: RolloverResult | null;
}

/**
 * Settle every unsettled day between the rollover watermark and
 * yesterday.
 *
 * WHY THIS EXISTS: `rolloverDay` had exactly one caller — RestartGate's
 * recovery branch — so for almost every user no day was ever folded into
 * the counters. `currentStreak` stayed 0 no matter how many days they
 * completed, which is the "Day 0" hero.
 *
 * ─────────────────────────────────────────────────────────────────────
 *  OWNER'S DECISION — READ BEFORE CHANGING THE SEEDING BELOW.
 *
 *  A challenge with NO `lastRolloverDate` is EVERY challenge that
 *  existed before this shipped. Its watermark is seeded to YESTERDAY,
 *  NOT to `startDate`.
 *
 *  Seeding to `startDate` would look more "correct" and would be
 *  catastrophic: a user on day 70 would have 70 days walked and
 *  re-judged against data this device may never have had, almost all of
 *  them failing, destroying a streak they actually earned. That is not
 *  a migration, it is data loss.
 *
 *  The rule is: history before this fix is never re-litigated. Correct
 *  behaviour starts from today forward. Do not "improve" this into a
 *  backfill.
 * ─────────────────────────────────────────────────────────────────────
 *
 * Other things this deliberately does NOT do:
 *   - It does not walk a non-active arc, but the three non-active
 *     statuses are NOT the same case and must not share a branch:
 *       · 'completed' — untouched entirely.
 *       · 'paused'    — watermark advanced to yesterday with nothing
 *                       evaluated, so a 30-day pause can't come back as
 *                       30 failed days when the user resumes.
 *       · 'failed'    — watermark left exactly where it is. It is parked
 *                       on the failed day and the days after it are
 *                       still owed; advancing it would throw them away.
 *   - It does not manufacture days. A date with no day doc returns null
 *     from the shared helper and is forgiven (see rolloverSingleDay).
 *   - It stops at the first failed day. That day needs a user decision
 *     (grace or restart) before anything after it means anything.
 *
 * NOT WIRED UP HERE. Per the plan there must be exactly ONE caller —
 * the `useChallenge` hook, after `getActiveChallenge` and before
 * `evaluateToday`. This function is exported and otherwise unreferenced
 * on purpose.
 */
export async function catchUpRollover(
  userId: string,
  challenge: Challenge,
  asOf: Date = new Date(),
): Promise<CatchUpResult> {
  const yesterdayStr = addLocalDays(localDateString(asOf), -1);
  const noop = (c: Challenge = challenge): CatchUpResult => ({
    challenge: c,
    days: [],
    pendingDecision: null,
  });

  // A finished arc is finished — never touch its counters or watermark.
  if (challenge.status === 'completed') return noop();

  // ── FAILED: park, don't forgive. ──────────────────────────────────
  // A dead arc is waiting on a user decision (grace / restart / a late
  // check-in that revives it). Its watermark is sitting ON the failed
  // day, exactly where the walk below left it, and that is load-bearing:
  // it is what lets the days AFTER the failure be settled once the arc
  // comes back.
  //
  // These two cases used to share one branch with the seed below, which
  // jumped the watermark straight to yesterday and evaluated nothing. So
  // every genuinely-passed day the user was about to revive was silently
  // discarded — and because the caller runs this on every mount, change
  // event and visibility change, it happened repeatedly while the user
  // sat on the failed screen looking at the decision.
  //
  // Return without touching the watermark. Note this is checked BEFORE
  // the seed: a failed arc that has never had a watermark keeps not
  // having one, so when it is revived the seed fires then and still
  // reads zero historical days.
  if (challenge.status === 'failed') return noop();

  // ── SEED: no watermark yet. ───────────────────────────────────────
  // Jump straight to yesterday and evaluate nothing. See the owner's-
  // decision block above — this is the "history is never re-litigated"
  // guarantee and it must not be weakened.
  if (!challenge.lastRolloverDate) {
    try {
      await updateChallenge(userId, challenge.id, { lastRolloverDate: yesterdayStr });
    } catch (e) {
      console.warn('[challenges] rollover watermark seed failed:', e);
      return noop();
    }
    return noop({ ...challenge, lastRolloverDate: yesterdayStr });
  }

  // ── FORGIVE: paused. ──────────────────────────────────────────────
  // A deliberately paused arc owes nothing for the time it was paused,
  // so its watermark advances to yesterday with nothing evaluated — a
  // 30-day pause must not come back as 30 failed days on resume.
  if (challenge.status !== 'active') {
    if (challenge.lastRolloverDate === yesterdayStr) return noop();
    try {
      await updateChallenge(userId, challenge.id, { lastRolloverDate: yesterdayStr });
    } catch (e) {
      console.warn('[challenges] rollover watermark forgive failed:', e);
      return noop();
    }
    return noop({ ...challenge, lastRolloverDate: yesterdayStr });
  }

  // Walk [max(startDate, watermark + 1) .. yesterday].
  const from = challenge.startDate > addLocalDays(challenge.lastRolloverDate, 1)
    ? challenge.startDate
    : addLocalDays(challenge.lastRolloverDate, 1);
  if (from > yesterdayStr) return noop();

  // Hard bound on the walk. Even with a corrupt watermark this can never
  // become an unbounded loop of Firestore transactions; whatever it
  // doesn't reach, the next call picks up from the new watermark.
  const cap = (challenge.duration ?? 0) + 30;

  let current: Challenge = challenge;
  let settledThrough = challenge.lastRolloverDate;
  const days: RolloverResult[] = [];
  let pendingDecision: RolloverResult | null = null;
  let cursor = from;

  for (let i = 0; i < cap && cursor <= yesterdayStr; i++) {
    let result: RolloverResult | null;
    try {
      result = await rolloverSingleDay(userId, current, cursor);
    } catch (e) {
      // Don't advance the watermark past a day we failed to settle —
      // the next call retries it cleanly.
      console.warn('[challenges] catch-up rollover failed on', cursor, e);
      break;
    }
    if (result && !result.settled) {
      // The outcome was computed but the write that banks it did not land.
      // Leave the watermark BEHIND this day so the next pass retries it —
      // advancing past it would forgive the day and lose its increment.
      console.warn('[challenges] catch-up rollover did not settle', cursor);
      break;
    }
    settledThrough = cursor;
    if (result) {
      days.push(result);
      // Thread the updated challenge forward: each day's counters are
      // the next day's starting point.
      current = result.updatedChallenge;
      if (result.needsRestartDecision) { pendingDecision = result; break; }
      if (current.status === 'completed') break;
    }
    cursor = addLocalDays(cursor, 1);
  }

  if (settledThrough !== challenge.lastRolloverDate) {
    try {
      await updateChallenge(userId, challenge.id, { lastRolloverDate: settledThrough });
      current = { ...current, lastRolloverDate: settledThrough };
    } catch (e) {
      // Counters are already persisted per-day by the shared helper, and
      // each day carries its own re-entry guard (rolloverAppliedAt /
      // terminal outcome), so a lost watermark costs a re-read next
      // time, not a double count.
      console.warn('[challenges] rollover watermark persist failed:', e);
    }
  }

  if (days.length > 0) emitChallengeChanged();
  return { challenge: current, days, pendingDecision };
}

/**
 * Mid-challenge commitment swap. Replaces `fromId` with `toId` in the
 * challenge's commitment list for the pillar `toId` belongs to.
 *
 * Guards:
 *   - Challenge must be active (paused / completed / failed can't swap)
 *   - Swap count must be below MAX_SWAPS (else throws — UI should
 *     have already disabled the button)
 *   - `fromId` must exist in the challenge's current commitments
 *   - `toId` must exist in COMMITMENT_LIBRARY and not already be in the
 *     challenge (no dupes)
 *
 * Side effects:
 *   - Updates challenge.commitments + swapsUsed in Firestore
 *   - Drops any completed[fromId] from today's DailyStatus so the
 *     new commitment starts fresh (otherwise a completed "yoga" being
 *     swapped to "ruck" would mark ruck instantly complete)
 *   - Re-runs evaluateToday so today's pillar counts reflect the swap
 */
export async function swapCommitment(
  userId: string,
  challenge: Challenge,
  fromId: string,
  toId: string,
): Promise<Challenge> {
  if (challenge.status !== 'active') {
    throw new Error('Swap only allowed on active challenges.');
  }
  const swapsUsed = challenge.swapsUsed ?? 0;
  if (swapsUsed >= MAX_SWAPS) {
    throw new Error(`Already used your ${MAX_SWAPS} swaps for this attempt.`);
  }

  // Locate which pillar owns fromId
  let owningPillar: Pillar | null = null;
  for (const p of ['physical', 'mental', 'spiritual'] as Pillar[]) {
    if (challenge.commitments[p].includes(fromId)) { owningPillar = p; break; }
  }
  if (!owningPillar) throw new Error(`Commitment ${fromId} not in this challenge.`);

  const newCommitment = COMMITMENT_LIBRARY.find(c => c.id === toId);
  if (!newCommitment) throw new Error(`Commitment ${toId} not in library.`);

  // Check no dup — a commitment can't appear twice across pillars
  const alreadyPresent =
    challenge.commitments.physical.includes(toId) ||
    challenge.commitments.mental.includes(toId) ||
    challenge.commitments.spiritual.includes(toId);
  if (alreadyPresent) throw new Error(`${newCommitment.label} is already a commitment.`);

  // Build the new commitments map. The replacement keeps the from
  // commitment's PILLAR (not the new commitment's natural pillar) — so
  // a Mental→Mental swap stays mental even if the new one was tagged
  // spiritual originally. This is rare given pillar-typed picks but
  // protects us from accidental cross-pillar migrations.
  const newCommitments = {
    physical: [...challenge.commitments.physical],
    mental: [...challenge.commitments.mental],
    spiritual: [...challenge.commitments.spiritual],
  };
  const idx = newCommitments[owningPillar].indexOf(fromId);
  newCommitments[owningPillar].splice(idx, 1, toId);

  const patch: Partial<Challenge> = {
    commitments: newCommitments,
    swapsUsed: swapsUsed + 1,
  };
  await updateChallenge(userId, challenge.id, patch);

  // Clean up today's status so the new commitment starts unchecked.
  try {
    const dateStr = localDateString();
    const today = (await getDailyStatus(userId, challenge.id, dateStr)) as OverridableStatus | null;
    const hadOverride = !!overridesOf(today)[fromId];
    if (today && (today.completed[fromId] || hadOverride)) {
      const completed = { ...today.completed };
      delete completed[fromId];
      // The swapped-out commitment's un-check goes with it — it can only
      // suppress detection for a commitment that is still on the arc.
      const manualOverrides = overridesOf(today);
      delete manualOverrides[fromId];
      // removeCommitmentIds is REQUIRED here: day writes merge, so
      // deleting the key locally is not enough — without the explicit
      // deleteField the swapped-out commitment would stay checked.
      await saveDailyStatus(
        userId,
        challenge.id,
        {
          ...today,
          completed,
          // `...today` carries the STORED override map, fromId included —
          // replace it with the pruned copy whenever the field exists at
          // all, and leave the field absent when it never did.
          ...(today.manualOverrides ? { manualOverrides } : {}),
          // Recompute tallies against the NEW commitment set, honouring
          // the same required-today filter every other write uses.
          pillarCounts: tallyPillars(
            { commitments: newCommitments, configOverrides: challenge.configOverrides },
            completed,
            new Date(),
          ),
        },
        { removeCommitmentIds: [fromId], ...(hadOverride ? { removeOverrideIds: [fromId] } : {}) },
      );
    }
  } catch (e) {
    console.warn('[swap] status cleanup failed:', e);
  }

  return { ...challenge, ...patch } as Challenge;
}

/**
 * Hard restart — reset counters, increment attempt number. Preserves
 * totalDaysCompleted and longestStreak (those accumulate across attempts).
 */
export async function restartChallenge(userId: string, challenge: Challenge): Promise<Challenge> {
  const startDate = localDateString();
  // The new attempt starts today, so nothing before today is ever in
  // scope for catch-up. Park the watermark on yesterday rather than
  // leaving the old attempt's value behind.
  const lastRolloverDate = addLocalDays(startDate, -1);
  const updated: Challenge = {
    ...challenge,
    attempt: challenge.attempt + 1,
    currentStreak: 0,
    graceDaysUsed: 0,
    startDate,
    lastRolloverDate,
    status: 'active',
  };
  await updateChallenge(userId, challenge.id, {
    attempt: updated.attempt,
    currentStreak: 0,
    graceDaysUsed: 0,
    startDate: updated.startDate,
    lastRolloverDate,
    status: 'active',
  });
  emitChallengeChanged();
  return updated;
}

/**
 * Mark a challenge as completed early. Used when the user wants to
 * gracefully end a challenge without restarting. Preserves career
 * stats (totalDaysCompleted, longestStreak) so future challenges see
 * the full history. Status flips to 'completed' which also stops the
 * notification scheduler.
 */
export async function endChallenge(userId: string, challenge: Challenge): Promise<void> {
  await updateChallenge(userId, challenge.id, {
    status: 'completed',
    completedAt: new Date().toISOString(),
  } as Partial<Challenge>);
  emitChallengeChanged();
}

/**
 * Hard delete a challenge — wipes the parent doc and every day doc.
 * Use only when the user explicitly chooses "Delete" (vs Pause/End).
 * Total-days-completed and longestStreak are LOST with this — caller
 * should confirm via UI.
 *
 * Fires `opmode-challenge-changed` after the delete so the dashboard
 * PillarMissionCard (and any other cached consumer) can invalidate
 * immediately instead of waiting up to 30s for the next poll tick.
 */
export async function deleteChallenge(userId: string, challengeId: string): Promise<void> {
  const { doc, deleteDoc, collection, getDocs } = await import('firebase/firestore');
  const { db } = await import('./firebase');
  // Delete every day doc in the subcollection first (Firestore doesn't
  // cascade — orphaned subcollection docs would linger and count toward
  // the user's storage forever).
  try {
    const daysRef = collection(db, 'users', userId, COLLECTION, challengeId, 'days');
    const daysSnap = await getDocs(daysRef);
    await Promise.all(daysSnap.docs.map(d => deleteDoc(d.ref)));
  } catch (e) {
    console.warn('[challenges] day-subcollection cleanup failed:', e);
  }
  // Cancel pending notifications before removing the parent doc — once
  // the doc is gone evaluateToday can't re-schedule with current state.
  try {
    const { cancelChallengeNotifications } = await import('./pillarChallengeNotifications');
    await cancelChallengeNotifications();
  } catch {}
  await deleteDoc(doc(db, 'users', userId, COLLECTION, challengeId));
  emitChallengeChanged();
}

/**
 * Hard delete EVERY active challenge for the user. Surfaces an admin
 * escape hatch for the "I accidentally created multiple challenges
 * because the old getActiveChallenge bug hid them all" case. Walks
 * all challenges (not just active) so completed/paused are also
 * cleared — caller decides whether they want that.
 */
export async function deleteAllChallenges(userId: string): Promise<number> {
  const { collection, getDocs, deleteDoc } = await import('firebase/firestore');
  const { db } = await import('./firebase');
  const ref = collection(db, 'users', userId, COLLECTION);
  const snap = await getDocs(ref);
  let deleted = 0;
  for (const c of snap.docs) {
    try {
      // Clear each challenge's days subcollection first
      const daysRef = collection(db, 'users', userId, COLLECTION, c.id, 'days');
      const daysSnap = await getDocs(daysRef);
      await Promise.all(daysSnap.docs.map(d => deleteDoc(d.ref)));
      await deleteDoc(c.ref);
      deleted++;
    } catch (e) {
      console.warn('[challenges] deleteAllChallenges item failed:', c.id, e);
    }
  }
  try {
    const { cancelChallengeNotifications } = await import('./pillarChallengeNotifications');
    await cancelChallengeNotifications();
  } catch {}
  emitChallengeChanged();
  return deleted;
}

/** Cache-invalidation event. PillarMissionCard + RestartGate listen
 *  for this and immediately re-fetch. Avoids the 30s poll lag. */
function emitChallengeChanged(): void {
  if (typeof window === 'undefined') return;
  try { window.dispatchEvent(new CustomEvent('opmode-challenge-changed')); } catch {}
}

/**
 * Use a grace day to save a failed day. Increments graceDaysUsed, banks
 * the day into the streak, and marks the day doc 'grace-used'.
 * Throws if no grace days remain.
 *
 * THREE THINGS THIS GOT WRONG BEFORE — all of them cost the user real,
 * finite resources, so this path is now transactional:
 *
 *   1. DOUBLE-TAP BURNED TWO GRACE DAYS. There was no idempotence check
 *      at all: two taps (or a tap on two devices) each read the same
 *      pre-state and each incremented. A day already marked 'grace-used'
 *      or 'passed' is settled — spending a second grace day on it is
 *      never right, so it now no-ops and returns the challenge unchanged.
 *   2. IT DIDN'T COUNT TOWARD totalDaysCompleted. The day counted toward
 *      currentStreak but not the career total, so the two numbers drifted
 *      apart by exactly the number of grace days used.
 *   3. IT STRANDED FAILED ARCS. Revival was gated on
 *      `challenge.status === 'failed'` read from the CALLER'S object.
 *      RestartGate loads the challenge on mount and then calls
 *      rolloverDay, which durably flips the stored status to 'failed' —
 *      but the gate's in-memory copy still says 'active'. So the grace
 *      day was spent, counters moved, and the stored doc stayed 'failed'
 *      forever, leaving restart (which discards the streak) as the only
 *      exit. The status is now written unconditionally from inside the
 *      transaction for any arc that isn't already 'completed'. It does
 *      not matter what the caller thinks the status is.
 */
export async function useGraceDay(userId: string, challenge: Challenge, yesterdayDate: string): Promise<Challenge> {
  if ((challenge.graceDaysUsed ?? 0) >= (challenge.graceDays ?? 0)) {
    throw new Error('No grace days remaining');
  }

  const graceDate = parseLocalDate(yesterdayDate);

  /** The fields a fresh grace-day day-doc needs when none exists yet.
   *  Writing one matters: without a doc there is no 'grace-used' marker,
   *  so the idempotence check above would have nothing to catch on and
   *  the day would stay re-graceable forever. */
  const newDayFields = (c: Challenge) => ({
    date: yesterdayDate,
    dayNumber: dayNumberFor(c, graceDate),
    completed: {},
    pillarCounts: tallyPillars(c, {}, graceDate),
  });

  const { doc, runTransaction, serverTimestamp } = await import('firebase/firestore');
  const { db } = await import('./firebase');
  const challengeRef = doc(db, 'users', userId, COLLECTION, challenge.id);
  const dayRef = doc(db, 'users', userId, COLLECTION, challenge.id, 'days', yesterdayDate);

  let updated: Challenge = { ...challenge };
  let applied = false;
  let statusChanged = false;
  let exhausted = false;

  const apply = (cur: Challenge) => {
    const graceDaysUsed = (cur.graceDaysUsed ?? 0) + 1;
    const currentStreak = (cur.currentStreak ?? 0) + 1; // grace still counts toward the streak
    const totalDaysCompleted = (cur.totalDaysCompleted ?? 0) + 1;
    const longestStreak = Math.max(cur.longestStreak ?? 0, currentStreak);
    // A grace day can be the day that finishes the arc — mirror the
    // rollover path rather than leaving a finished arc sitting 'active'.
    const finished = currentStreak >= cur.duration;
    const status: Challenge['status'] = finished ? 'completed' : 'active';
    const completedAt = finished ? new Date().toISOString() : cur.completedAt;
    return { graceDaysUsed, currentStreak, totalDaysCompleted, longestStreak, status, completedAt };
  };

  try {
    await runTransaction(db, async tx => {
      // Firestore re-runs this callback on contention — reset every
      // outer flag so a discarded attempt can't leak into the result.
      applied = false;
      statusChanged = false;
      exhausted = false;

      const chSnap = await tx.get(challengeRef);
      if (!chSnap.exists()) return;
      const cur = chSnap.data() as Challenge;
      updated = { ...cur };
      if (cur.status === 'completed') return; // nothing left to save
      if ((cur.graceDaysUsed ?? 0) >= (cur.graceDays ?? 0)) { exhausted = true; return; }

      const daySnap = await tx.get(dayRef);
      const day = daySnap.exists() ? (daySnap.data() as DailyStatus) : null;
      // Already settled — the second tap of a double-tap lands here.
      if (day?.outcome === 'grace-used' || day?.outcome === 'passed') return;

      const next = apply(cur);
      tx.update(challengeRef, {
        graceDaysUsed: next.graceDaysUsed,
        currentStreak: next.currentStreak,
        totalDaysCompleted: next.totalDaysCompleted,
        longestStreak: next.longestStreak,
        status: next.status,
        ...(next.completedAt ? { completedAt: next.completedAt } : {}),
        updatedAt: serverTimestamp(),
      } as any);
      tx.set(
        dayRef,
        {
          ...(day ? {} : newDayFields(cur)),
          outcome: 'grace-used',
          updatedAt: serverTimestamp(),
        } as any,
        { merge: true },
      );

      applied = true;
      statusChanged = next.status !== cur.status;
      updated = { ...cur, ...next, completedAt: next.completedAt };
    });
  } catch (e) {
    // Transactions reject rather than queue when offline. Fall back to
    // the read-modify-write path so a grace day still works on a bad
    // connection — with the same idempotence check, so the fallback
    // can't reintroduce the double-burn.
    console.warn('[challenges] useGraceDay transaction failed, falling back:', e);
    const day = await getDailyStatus(userId, challenge.id, yesterdayDate);
    if (day?.outcome === 'grace-used' || day?.outcome === 'passed') return { ...challenge };
    if (challenge.status === 'completed') return { ...challenge };
    const next = apply(challenge);
    await saveDailyStatus(userId, challenge.id, {
      ...(day ?? (newDayFields(challenge) as unknown as DailyStatus)),
      outcome: 'grace-used',
    } as DailyStatus);
    await updateChallenge(userId, challenge.id, {
      graceDaysUsed: next.graceDaysUsed,
      currentStreak: next.currentStreak,
      totalDaysCompleted: next.totalDaysCompleted,
      longestStreak: next.longestStreak,
      status: next.status,
      ...(next.completedAt ? { completedAt: next.completedAt } : {}),
    });
    emitChallengeChanged();
    return { ...challenge, ...next, completedAt: next.completedAt };
  }

  if (exhausted) throw new Error('No grace days remaining');
  if (applied) emitChallengeChanged();
  // updateChallenge() cancels pending reminders on any status change; the
  // raw transaction above bypasses it, so mirror that here.
  if (statusChanged) {
    try {
      const { cancelChallengeNotifications } = await import('./pillarChallengeNotifications');
      void cancelChallengeNotifications();
    } catch {}
  }
  return updated;
}

// ============================================================================
// PRESET → CHALLENGE BOOTSTRAP
// ============================================================================

/**
 * Construct an unpersisted Challenge object from a preset. Caller can
 * tweak (rename, adjust commitments) before calling createChallenge.
 */
export function buildChallengeFromPreset(preset: Preset, opts?: {
  customName?: string;
  customDuration?: number;
  customCommitments?: { physical: string[]; mental: string[]; spiritual: string[] };
  configOverrides?: Record<string, Record<string, unknown>>;
  why?: { text: string; displayMode: 'rotate' | 'decision-only' | 'silent' };
  notifications?: { morningBriefing: boolean; earlyEvening?: boolean; eveningWarning: boolean; finalCall: boolean };
}): Omit<Challenge, 'id' | 'userId' | 'attempt' | 'totalDaysCompleted' | 'currentStreak' | 'longestStreak' | 'createdAt' | 'updatedAt' | 'status'> {
  return {
    presetId: preset.id,
    name: opts?.customName ?? preset.name,
    duration: opts?.customDuration ?? preset.duration,
    startDate: localDateString(),
    commitments: opts?.customCommitments ?? preset.commitments,
    // Seed from the preset's baked-in overrides (e.g. Reconditioning's
    // 3×/week training), then let any caller-supplied overrides win.
    configOverrides: { ...(preset.configOverrides ?? {}), ...(opts?.configOverrides ?? {}) },
    failureMode: preset.failureMode,
    graceDays: preset.graceDays,
    graceDaysUsed: 0,
    why: opts?.why ?? null,
    notifications: opts?.notifications ?? {
      morningBriefing: true,
      earlyEvening: true,
      eveningWarning: true,
      finalCall: true,
    },
  };
}
